import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Modal } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFocusOnOpen } from "../../hooks/useFocusOnOpen";
import { useViewport } from "../../hooks/useViewport";
import {
  describeLicenseNotice,
  type PendingLicenseNotice,
} from "../../lib/licenseNotice";
import { modalStyles, modalWrapStyle } from "../../utils/modalStyles";

interface LicenseNoticeModalProps {
  open: boolean;
  pending: PendingLicenseNotice;
  /** Човекът има „Справки"/„Склад": след гратиса броячът става „Само преглед". */
  readOnlyAfterGrace: boolean;
  onAcknowledge: () => void;
  afterClose: () => void;
}

/** Колко често се опреснява броячът („5 ч 55 мин"), докато модалът стои — всяка
 * секунда, за да се смени веднага щом мине минута. */
const COUNTDOWN_TICK_MS = 1_000;

/** Текущият момент, опресняван на `COUNTDOWN_TICK_MS`, докато `active`. */
function useNow(active: boolean): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!active) return;
    const tick = () => setNow(new Date());
    // Веднага при отваряне — модалът може да е чакал скрит (напр. на /monitors).
    const first = window.setTimeout(tick, 0);
    const timer = window.setInterval(tick, COUNTDOWN_TICK_MS);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [active]);
  return now;
}

// Предупреждението за лиценза веднага след входа в общия шел на модалите:
// шапка с икон чип, „Достъпът ще спре след 5 ч 55 мин" (броячът тече, докато
// модалът стои) и датата на изтичане; в тялото призив да се поднови и един
// широк бутон „Разбрах". „Достъпът е спрян" и „Само преглед" — центрирана карта.
// Нарочно без close чип, без затваряне от фона и от Esc: единственото, което
// може да се натисне, е „Разбрах" (изрично искане на потребителя).
export default function LicenseNoticeModal({
  open,
  pending,
  readOnlyAfterGrace,
  onAcknowledge,
  afterClose,
}: LicenseNoticeModalProps) {
  const { t } = useTranslation();
  const { isMobile, isNarrow } = useViewport();
  const now = useNow(open);
  const texts = describeLicenseNotice(pending, t, now, readOnlyAfterGrace);
  // „Достъпът е спрян" и „Само преглед" — центрирана карта без брояч (дизайн
  // на потребителя 1:1); предупрежденията с брояч остават в шела с шапка.
  const stopped = texts.variant === "blocked" || texts.variant === "readOnly";
  // 500 — „Достъпът ще спре след 167 ч 59 мин" се събира на един ред.
  const width = isMobile ? undefined : stopped ? 440 : 500;
  // Изтекъл лиценз (гратис) — червената иконка; преди изтичането — кехлибарената.
  const urgent = texts.variant !== "warning";
  // Единственият бутон взима фокуса — „Разбрах" става и с Enter (касите с
  // клавиатура), а Esc нарочно не затваря. По един пазач на изглед: брояч,
  // обърнат в „спрян" докато модалът стои, монтира нов бутон и той пак взима фокуса.
  const acknowledgeRef = useRef<HTMLButtonElement>(null);
  useFocusOnOpen(open && stopped, acknowledgeRef);
  useFocusOnOpen(open && !stopped, acknowledgeRef);
  // Enter на „Разбрах" не стига до слушателите на прозореца под модала (Enter =
  // „Печат" в отчета би отменил натискането и показал модала „Само преглед" пак).
  const keepEnterOnButton = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter") event.stopPropagation();
  };

  // Esc не затваря модала, но и не бива да мине през глобалната навигация
  // „назад" (SharedAccountGate) — затова само preventDefault.
  useEffect(() => {
    if (!open) return;
    const swallowEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") event.preventDefault();
    };
    window.addEventListener("keydown", swallowEscape);
    return () => window.removeEventListener("keydown", swallowEscape);
  }, [open]);

  return (
    <Modal
      open={open}
      footer={null}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      afterClose={afterClose}
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
      aria-labelledby="license-notice-title"
    >
      {stopped ? (
        <div
          className={`flex flex-col items-center bg-surface text-center ${
            isNarrow ? "px-4 pb-4 pt-4" : "px-6 pb-6 pt-5"
          }`}
        >
          {/* Иконката в двоен кръг: светъл ореол около tint кръга. */}
          <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-danger-tint/45">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-danger-tint text-danger-ink">
              <SafetyCertificateOutlined className="text-[20px]" />
            </div>
          </div>
          <h2
            id="license-notice-title"
            className="m-0 mt-2.5 text-[20px] font-bold leading-[1.15] text-ink"
          >
            {texts.title}
          </h2>
          {/* Датата на изтичане — червено хапче под заглавието. */}
          <p className="m-0 mt-3 rounded-full bg-danger-tint/45 px-2.5 py-1 text-[12px] font-medium leading-4 text-danger-deep-ink">
            {texts.subtitle}
          </p>
          <p className="m-0 mt-4 px-2 text-[14px] leading-[1.6] text-ink-2">
            {texts.message}
          </p>
          {/* Фокусът остава (Enter = „Разбрах"), но без синята рамка на браузъра. */}
          <button
            ref={acknowledgeRef}
            type="button"
            onClick={onAcknowledge}
            onKeyDown={keepEnterOnButton}
            className="mt-6 flex h-11 w-full cursor-pointer items-center justify-center rounded-[10px] border-none bg-brand px-[22px] text-[15px] font-bold text-white outline-none transition-colors duration-150 hover:bg-brand-deep"
          >
            {t("licenseNotice.modal.acknowledge")}
          </button>
        </div>
      ) : (
      <div className="bg-surface text-ink">
        {/* Шапката на общия шел (modals.md): икон чип + заглавие + дата,
            долна линия — само без close чип. На тесен екран (телефон)
            заглавието не се събира на ред: иконката е отгоре, а фразата и
            броячът са на два центрирани реда. */}
        <div
          className={`flex gap-3 border-b border-shade-2 ${
            isNarrow
              ? "flex-col items-center px-4 py-4 text-center"
              : "items-start px-6 py-5"
          }`}
        >
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${
              urgent
                ? "bg-danger-tint text-danger-ink"
                : "bg-brand-tint text-brand-deep-ink"
            }`}
          >
            <SafetyCertificateOutlined className="text-[20px]" />
          </div>
          <div className={isNarrow ? "w-full" : "min-w-0 flex-1"}>
            <h2
              id="license-notice-title"
              className={`m-0 text-[18px] font-bold leading-[1.25] tracking-[-0.3px] text-ink ${
                isNarrow ? "" : "whitespace-nowrap"
              }`}
            >
              {texts.title}
              {texts.countdown && (
                <>
                  {" "}
                  {/* Броячът — в цвета на иконката и с еднакво широки цифри,
                      за да не подскача, докато отброява; на тесен екран е
                      на собствен ред. */}
                  <span
                    className={`whitespace-nowrap tabular-nums ${
                      isNarrow ? "block" : ""
                    } ${urgent ? "text-danger-ink" : "text-brand-deep-ink"}`}
                  >
                    {texts.countdown}
                  </span>
                </>
              )}
            </h2>
            <p className="m-0 mt-1 text-[13px] font-medium leading-[1.4] text-body">
              {texts.subtitle}
            </p>
          </div>
        </div>

        <div className={isNarrow ? "p-4" : "px-6 pb-6 pt-5"}>
          <p
            className={`m-0 text-[14px] leading-[1.5] text-body ${
              isNarrow ? "text-center" : ""
            }`}
          >
            {texts.message}
          </p>
          {/* Фокусът остава (Enter = „Разбрах"), но без синята рамка на браузъра. */}
          <button
            ref={acknowledgeRef}
            type="button"
            onClick={onAcknowledge}
            onKeyDown={keepEnterOnButton}
            className="mt-5 flex h-11 w-full cursor-pointer items-center justify-center rounded-[10px] border-none bg-brand px-[22px] text-[14px] font-semibold text-white outline-none transition-colors duration-150 hover:bg-brand-deep"
          >
            {t("licenseNotice.modal.acknowledge")}
          </button>
        </div>
      </div>
      )}
    </Modal>
  );
}
